import type * as React from "react";
import {
  Banknote,
  Bot,
  Calendar as CalendarIcon,
  FileBarChart,
  FileEdit,
  FilePlus2,
  FileText,
  ListTodo,
  Mail,
  Receipt,
  Reply,
  Send,
  Shield,
  ShoppingCart,
  UserPlus,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import {
  actionCategory,
  type ActionCategoryIcon,
} from "../actionTypeToTab";

/**
 * action_type → lucide icon mapping.
 *
 * The mapping is intentionally coarse-grained (one glyph per CATEGORY of
 * action, not per vendor) so the badge in ``FocusedActionView`` reads as
 * "what is happening" rather than "which vendor". The vendor label rides
 * the eyebrow line (``GMAIL · ORA``) — DRY with the inbox/calendar
 * pattern where the icon describes the medium (Mail / Calendar / etc.)
 * and the eyebrow surfaces the brand + time context.
 *
 * Fallback: ``Mail`` (every action carries a "message" semantics — see
 * {@link iconForActionType}). When you add a new BE ``action_type``,
 * extend this map rather than passing a per-callsite icon.
 */
const ICON_BY_ACTION_TYPE: Readonly<Record<string, LucideIcon>> = {
  // Email / chat / PEC — Mail glyph for compose, Reply for replies.
  // BE-canonical wire is ``send_email``; the legacy alias survives.
  send_email: Reply,
  email_send_draft: Reply,
  aruba_pec_send: Shield,
  infocert_pec_send: Shield,
  fic_schedule_invoice_email: Send,
  // Calendar
  calendar_create_event: CalendarIcon,
  // Tasks & automations
  task_create: ListTodo,
  automation_trigger: Bot,
  // Fatture in Cloud / Acube / SAP Finance
  fic_create_invoice: Receipt,
  fic_credit_note: Receipt,
  fic_payment: Wallet,
  submit_invoice_sdi: Receipt,
  register_cassetto: Receipt,
  archive_conservazione: Shield,
  send_peppol: Send,
  // Open Banking — SEPA credit transfer reads as money movement, not a fiscal doc.
  initiate_sepa_payment: Banknote,
  sap_create_invoice: Receipt,
  // CRM / ERP
  salesforce_create_lead: UserPlus,
  salesforce_create_opportunity: UserPlus,
  salesforce_create_contact: UserPlus,
  salesforce_create_account: UserPlus,
  hubspot_create_deal: UserPlus,
  hubspot_create_contact: UserPlus,
  sap_create_purchase_order: ShoppingCart,
  // Documents
  document_edit: FileEdit,
  document_fork_edit: FileEdit,
  document_create: FilePlus2,
  // Voice-of-X report generation (Workstream B M2.5, June 17, 2026) — the
  // HITL card composes a report DEFINITION + first RUN in one confirm.
  voice_report_generate: FileBarChart,
};

/**
 * Maps the SHARED action-category icon kind (from {@link actionCategory},
 * the single taxonomy that also drives tab routing) to a lucide glyph. This
 * is the ONLY place the kind→glyph binding lives, so tab and icon read the
 * same prefix rules and can never diverge.
 *
 * ``initiate_sepa_payment`` is a SEPA bonifico → ``Banknote`` (money
 * movement, not a fiscal doc) via its exact entry above; the generic
 * ``payment`` kind glyph is ``Wallet`` (incassi/pagamenti FiC).
 */
const ICON_BY_CATEGORY: Readonly<Record<ActionCategoryIcon, LucideIcon>> = {
  payment: Wallet,
  fiscal_doc: Receipt,
  certified: Shield,
  message: Mail,
  crm: UserPlus,
  procurement: ShoppingCart,
  document: FileEdit,
  calendar: CalendarIcon,
};

/**
 * Resolve the lucide icon for a BE action_type. Returns ``Mail`` as a
 * conservative fallback when the type is unknown (every action proposal
 * has a "message" semantics one way or another — better than an
 * abstract glyph which competes with the agent's "AI" identity).
 */
export function iconForActionType(actionType: string): LucideIcon {
  const exact = ICON_BY_ACTION_TYPE[actionType];
  if (exact) return exact;
  // Shared prefix taxonomy for dynamic cross-vendor CRUD wire strings
  // (``salesforce_update_lead``, ``fatture_in_cloud_create_cliente``, …) so
  // the badge stays meaningful without a per-sobject map entry — and so it
  // always agrees with the routed tab.
  const category = actionCategory(actionType);
  if (category) return ICON_BY_CATEGORY[category.icon];
  return Mail;
}

/**
 * Convenience render — returns the JSX node already sized for the
 * ``FocusedViewLayout`` badge slot (3.5 × 3.5 px lucide stroke at 2px).
 * Matches the inline calendar / inbox usage so the badge looks identical
 * across surfaces.
 */
export function renderActionTypeIcon(
  actionType: string,
  className = "h-3.5 w-3.5",
): React.ReactNode {
  const Icon = iconForActionType(actionType);
  return <Icon className={className} strokeWidth={2} aria-hidden />;
}

// Re-export FileText so callers can use it without a second import for
// the "generic action" fallback header in FocusedActionView.
export { FileText };
